import React, { useState, useEffect, useRef } from 'react';
// import Papa from 'papaparse'; // Removed CSV

import html2canvas from 'html2canvas';
import ExcelJS from 'exceljs';
import Card from '../../../ui/Card';
import Icon from '../../../ui/Icon';
import { ICONS } from '../../../ui/icon-map';
import Button from '../../../ui/Button';
import Dialog from '../../../ui/Dialog/Dialog';
import StatusBadge from '../../../ui/Status/StatusBadge';
import Stat, { StatGrid } from '../../../ui/Stat/Stat';
import { Field, Input, FormGrid } from '../../../ui/Field/Field';
import { toast } from '../../../ui/feedback';
import { TableContainer, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../../../ui/Table/Table';
import { CellText, CellPlate, CellNumber } from '../../../ui/DataTable/cells';
import RequirePermission from '../../../components/RequirePermission';
import { PERMISSIONS } from '../../../constants/permissions';
import logger from '../../../utils/logger';
import { contractsService } from '../../../services/firestore'; // Import Services
import { parseDateTime, formatDateTime } from '../../../services/possessionMatcher';
import { useAuth } from '../../../contexts/AuthContext';
import { adminService } from '../../../services/adminService';
import { useCompanySettings } from '../../../services/companySettings';
import InvoiceNumberField, { useInvoiceNumber } from '../../../components/InvoiceNumberField';
import { UNKNOWN_STATUS } from '../../../services/contractRules';
import { formatContractDateTime } from '../../../utils/contractDateUtils';
import { deskInvoiceAmounts, deskInvoiceHtml, saveDeskInvoicePdf, saveWorkbook, addReportHeader, escapeHtml } from './deskInvoice';
import { mileageAllowance } from './mileage.logic';

const MileageCalculator = () => {
    const { currentUser } = useAuth();
    const { settings: companySettings } = useCompanySettings();
    const [logs, setLogs] = useState([]);
    const [out, setOut] = useState('');
    const [inVal, setInVal] = useState('');
    const [date, setDate] = useState('');
    const [lastDate, setLastDate] = useState('');
    // the earliest contract's own Pick-up Date behind lastDate (keeps its time for display)
    const [startRaw, setStartRaw] = useState('');
    const [dateLocked, setDateLocked] = useState(false);
    const [booking, setBooking] = useState('');

    // Multi-vehicle state
    const [contractHistory, setContractHistory] = useState([]); // Array of all contracts for this booking
    const [vehicleLogs, setVehicleLogs] = useState({}); // Map: contractId -> { out: '', in: '' }

    const [contractData, setContractData] = useState(null); // Keep for main display (latest or primary)
    const [contractSource, setContractSource] = useState('');
    const [error, setError] = useState('');

    const [inputError, setInputError] = useState('');
    const [manualEndDate, setManualEndDate] = useState('');
    // the contract's own drop-off value behind manualEndDate (keeps its time for display); '' = today / none
    const [endRaw, setEndRaw] = useState('');
    const [showRefModal, setShowRefModal] = useState(false);
    // numbering while the shared counter is off (as before): ALWFQ-<typed>, nothing typed = 'ALWFQ-'
    const number = useInvoiceNumber(showRefModal, 0, { prefix: 'ALWFQ-', blank: '' });
    const [syncedFields, setSyncedFields] = useState({}); // { [contractId_field]: true }

    const outInputRef = useRef(null);

    // Parse date helper -> 'YYYY-MM-DD' (accepts strings, Dates and Firestore Timestamps)
    const parseCustomDate = (value) => {
        if (value === null || value === undefined || value === '') return null;
        if (typeof value === 'string') {
            const dateStr = value.trim();
            if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) return dateStr;
            const match = dateStr.match(/^(\d{2})[/-](\d{2})[/-](\d{4})/);
            if (match) {
                const [_, day, month, year] = match;
                return `${year}-${month}-${day}`;
            }
        }
        const d = parseDateTime(value);
        if (!d) return null;
        const pad = (n) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    };

    const formatDateToDMY = (dateStr) => {
        if (!dateStr) return '';
        if (typeof dateStr !== 'string') {
            const d = parseDateTime(dateStr);
            return d ? formatDateTime(d).split(' ')[0] : '';
        }
        // If already has slashes (e.g. 30/01/2025), just return it (maybe strip time if needed)
        if (dateStr.includes('/')) return dateStr.split(' ')[0]; // Return DD/MM/YYYY only? Or keep time? User didn't complain about time, but "undefined/undefined/..."
        // If it's ISO (YYYY-MM-DD or YYYY-MM-DDTHH:mm)
        if (dateStr.includes('-')) {
            const [datePart] = dateStr.split('T');
            const parts = datePart.split('-');
            if (parts.length === 3) {
                const [year, month, day] = parts;
                return `${day}/${month}/${year}`;
            }
        }
        return dateStr;
    };

    // A contract pick-up / drop-off for display: "dd/mm/yyyy HH:MM" when the value has a time,
    // "dd/mm/yyyy" when it has none (never an invented time); unreadable values as before.
    const showContractDate = (value) => formatContractDateTime(value) || formatDateToDMY(value);

    useEffect(() => {
        const fetchContracts = async () => {
            if (booking.trim() === '') {
                setContractData(null);
                setContractHistory([]);
                setVehicleLogs({});
                setError('');
                setContractSource('');
                setSyncedFields({});
                return;
            }

            // setIsLoading(true); // This variable is not defined in the provided code.
            try {
                const history = await contractsService.getContractsByBookingNumber(booking.trim());
                
                if (history && history.length > 0) {
                    setContractHistory(history);

                    // Determine Overall Status
                    // If ANY segment is open -> Open.
                    // If ALL closed -> Closed. (Usually the last one determines it)
                    // The history is likely sorted Descending by Pickup (Newest first) in firestore.js
                    // But let's safely find the 'latest' by date to be sure.

                    const sortedByDate = [...history].sort((a, b) => {
                        const getTs = (c) => {
                            const d = c._segment?.start || parseDateTime(c['Pick-up Date']);
                            return d ? d.getTime() : 0;
                        };
                        return getTs(b) - getTs(a); // Descending
                    });

                    const latest = sortedByDate[0];
                    const earliest = sortedByDate[sortedByDate.length - 1];

                    setContractData(latest); // Use latest for Customer/Contract No info

                    const status = latest['Status'] || UNKNOWN_STATUS;
                    const dropOffRaw = latest['Drop-off Date'];
                    const hasDropOff = typeof dropOffRaw === 'string' ? dropOffRaw.trim() !== '' : Boolean(parseDateTime(dropOffRaw));
                    const isClosed = String(status).toLowerCase().includes('close') || hasDropOff;

                    if (isClosed) {
                        setContractSource('Closed Contract');
                    } else {
                        setContractSource('Open Contract');
                    }

                    // --- DATE LOGIC FOR HEADER ---
                    // Start Date = Earliest Pickup
                    // End Date = Latest Drop-off (if Closed) or "Open"

                    if (earliest && earliest['Pick-up Date']) {
                        const rawStart = earliest['Pick-up Date'];
                        const parsedStart = parseCustomDate(rawStart);
                        if (parsedStart) {
                            setLastDate(parsedStart);
                            setStartRaw(rawStart);
                            setDateLocked(true);
                        } else {
                            setLastDate('');
                            setStartRaw('');
                            setDateLocked(false);
                        }
                    }

                    // a closed contract without a readable drop-off date leaves the end empty (typed below),
                    // never the previous booking's end date
                    setManualEndDate('');
                    setEndRaw('');
                    if (isClosed) {
                        if (latest['Drop-off Date']) {
                            const parsedEnd = parseCustomDate(latest['Drop-off Date']);
                            if (parsedEnd) { setManualEndDate(parsedEnd); setEndRaw(latest['Drop-off Date']); }
                        } else if (latest['Close Date']) {
                            const parsedEnd = parseCustomDate(latest['Close Date']);
                            if (parsedEnd) { setManualEndDate(parsedEnd); setEndRaw(latest['Close Date']); }
                        }
                    } else {
                        setManualEndDate(new Date().toISOString().split('T')[0]);
                    }

                    // --- KM AUTOMATION ---
                    // Each history record is one possession segment; the service attaches the
                    // E-Check readings that bound that segment (`_readings`, see
                    // possessionMatcher.segmentReadings), so a plate used twice (A -> B -> A)
                    // gets its own readings for each stint instead of the same pair twice.
                    const logUpdates = {};
                    const syncUpdates = {};

                    history.forEach(car => {
                        const readings = car._readings || { out: null, in: null, km: null };
                        const startKM = typeof readings.out?.odometer === 'number' ? readings.out.odometer : undefined;
                        const endKM = typeof readings.in?.odometer === 'number' ? readings.in.odometer : undefined;

                        // Inject Dual E-Check Numbers from the segment readings
                        if (readings.out?.echeckNo) {
                            car['E-Check Number Out'] = readings.out.echeckNo;
                        }
                        if (readings.in?.echeckNo) {
                            car['E-Check Number In'] = readings.in.echeckNo;
                        }

                        logUpdates[car.id] = {
                            out: startKM !== undefined ? startKM : 0,
                            in: endKM !== undefined ? endKM : 0
                        };

                        if (startKM !== undefined) syncUpdates[`${car.id}_out`] = true;
                        if (endKM !== undefined) syncUpdates[`${car.id}_in`] = true;
                    });

                    setVehicleLogs(logUpdates);
                    setSyncedFields(syncUpdates);

                    if (history.length === 1) {
                        const firstCarId = history[0].id;
                        setOut(logUpdates[firstCarId]?.out || 0);
                        setInVal(logUpdates[firstCarId]?.in || 0);
                    }
                    setError('');
                } else {
                    setContractData(null);
                    setContractHistory([]);
                    setVehicleLogs({});
                    setSyncedFields({});
                    setError(' No data found for the entered number');
                }
            } catch (err) {
                logger.error('Error fetching contract in calculator', err);
                setError('Error fetching data');
            } finally {
                // setIsLoading(false); // This variable is not defined in the provided code.
            }
        };

        // Debounce search
        const timeoutId = setTimeout(() => {
            if (booking) fetchContracts();
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [booking]);

    // Update LocalStorage Logic to include new state if needed
    // (Skipping for now to keep it simple, or clear it on new booking)
    React.useEffect(() => {
        if (booking.trim() !== '') {
            setLogs([]);
            setOut(''); setInVal(''); setDate('');
            setVehicleLogs({}); // Clear vehicle inputs
        }
    }, [booking]);

    const handleAddLog = () => {
        const logDate = date || lastDate;
        if (!logDate || !out || !inVal) {
            setInputError('Please enter all fields.');
            return;
        }
        const outNum = Number(out);
        const inNum = Number(inVal);
        if (isNaN(outNum) || isNaN(inNum) || outNum < 0 || inNum < 0) {
            setInputError('OUT and IN must be positive numbers.');
            return;
        }
        if (outNum > inNum) {
            setInputError('OUT cannot be greater than IN.');
            return;
        }
        setLogs([...logs, {
            date: logDate,
            out: outNum,
            inVal: inNum,
            plate: contractData?.['Plate No.'] || contractData?.['Plate Number'] || contractData?.['Plate No'] || '',
            model: contractData?.['Model'] || contractData?.['Car Model'] || ''
        }]);
        setOut(''); setInVal('');
        if (!dateLocked) {
            setLastDate(logDate);
            setDateLocked(true);
        }
        setDate('');
        setInputError('');
        if (outInputRef.current) outInputRef.current.focus();
    };

    const handleVehicleLogChange = (id, field, value) => {
        setVehicleLogs(prev => ({
            ...prev,
            [id]: {
                ...prev[id],
                [field]: value
            }
        }));
        // Remove sync indicator if user manually changes the field
        setSyncedFields(prev => {
            const newState = { ...prev };
            delete newState[`${id}_${field}`];
            return newState;
        });
    };

    // Calculate Total KM from Multi-Vehicle Inputs
    const calculateTotalUsage = () => {
        let total = 0;
        contractHistory.forEach(c => {
            const log = vehicleLogs[c.id];
            if (log && log.out && log.in) {
                const start = Number(log.out);
                const end = Number(log.in);
                if (!isNaN(start) && !isNaN(end) && end >= start) {
                    total += (end - start);
                }
            }
        });
        return total;
    };

    // Fallback: If no multi-vehicle inputs used (legacy single input), use 'logs'
    // But user wants multi-vehicle. Let's make the UI drive this.
    // If 'contractHistory' > 1, show per-vehicle inputs. 
    // If single, maybe keep existing? No, user wants consistent behavior.

    const totalUsedKm = calculateTotalUsage();

    const getFirstDate = () => {
        // Find earliest pickup date in history
        if (contractHistory.length === 0) return null;
        const sorted = [...contractHistory].sort((a, b) => {
            const dA = parseCustomDate(a['Pick-up Date']) || '9999-99-99';
            const dB = parseCustomDate(b['Pick-up Date']) || '9999-99-99';
            return dA.localeCompare(dB);
        });
        const first = sorted[0];
        return parseCustomDate(first['Pick-up Date']);
    };

    // 2,500 km per 30 days from the first pick-up to the end date (ADM-17): without both dates there is no
    // allowance (null) - it used to be 0, so every kilometre was charged when the end date was missing.
    const startYmd = getFirstDate() || lastDate || null;
    const allowance = mileageAllowance(startYmd, manualEndDate, totalUsedKm);
    const allowedKm = allowance.allowedKm;
    const exceeded = allowance.exceededKm ?? 0;
    const missingDates = allowedKm === null;

    // A contract start / end date ('yyyy-mm-dd' state) as shown: the contract's own value with its time
    // while the date is still that value's day (not typed over), else the date alone
    const shownWithTime = (ymd, raw) => (ymd && raw && parseCustomDate(raw) === ymd ? formatContractDateTime(raw) : '')
        || (ymd ? formatDateToDMY(ymd) : '');
    const startShown = shownWithTime(lastDate, startRaw);
    const endShown = shownWithTime(manualEndDate, endRaw);

    // Export functions (Simplified for brevity, but retaining functionality)
    const exportToExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet('Mileage Report');

        // Add Header Info
        addReportHeader(sheet, {
            title: 'Mileage Report - ' + (contractData?.['Booking Number'] || ''),
            customer: contractData?.['Customer'],
            period: (startShown || 'N/A') + ' - ' + (endShown || 'N/A'),
        });

        // Table Header
        sheet.addRow(['#', 'Vehicle Plate', 'Type', 'Out (KM)', 'In (KM)', 'Distance', 'Period']);

        // Data Rows
        let currentTotalUsed = 0;
        if (contractHistory.length > 0) {
            contractHistory.forEach((c, idx) => {
                const log = vehicleLogs[c.id];
                if (log && log.out && log.in) {
                    const outNum = Number(log.out);
                    const inNum = Number(log.in);
                    const diff = inNum - outNum;
                    currentTotalUsed += diff;

                    const plate = c['Plate No.'] || c['Plate Number'] || '';
                    const model = c['Model'] || c['Car Model'] || '';
                    const period = (showContractDate(c['Pick-up Date']) || 'N/A') + ' - ' + (showContractDate(c['Drop-off Date']) || 'Active');

                    sheet.addRow([idx + 1, plate, model, outNum, inNum, diff, period]);
                }
            });
        } else {
            logs.forEach((log, idx) => {
                const diff = log.inVal - log.out;
                currentTotalUsed += diff;
                sheet.addRow([idx + 1, log.plate || '', log.model || '', log.out, log.inVal, diff, log.date || '']);
            });
        }

        sheet.addRow([]);
        sheet.addRow(['SUMMARY']);
        sheet.addRow(['Total Used:', currentTotalUsed + ' KM']);
        sheet.addRow(['Total Allowed:', missingDates ? 'N/A (no end date)' : allowedKm + ' KM']);
        sheet.addRow(['Exceeded KM:', missingDates ? 'N/A (no end date)' : exceeded + ' KM']);

        // Styling
        sheet.getRow(1).font = { bold: true, size: 14 };
        sheet.getRow(5).font = { bold: true };
        sheet.getColumn(1).width = 5;
        sheet.getColumn(2).width = 20;
        sheet.getColumn(3).width = 20;
        sheet.getColumn(4).width = 12;
        sheet.getColumn(5).width = 12;
        sheet.getColumn(6).width = 12;
        sheet.getColumn(7).width = 30;

        // Save Excel
        const bookingId = contractData?.['Booking Number'] || 'Report';
        await saveWorkbook(workbook, `Mileage-Report-${bookingId}.xlsx`);

        adminService.logUserAction('GENERATE_REPORT', 'Generated Mileage Report (Excel)', currentUser, { bookingId: bookingId, contractNo: contractData?.['Contract No.'] });
    };

    // Export Data as Image
    const exportAsImage = () => {
        const element = document.getElementById('export-section');
        if (!element) return;

        // Temporarily hide the search input during export
        const noExportElements = element.querySelectorAll('.no-export');
        noExportElements.forEach(el => el.style.display = 'none');

        html2canvas(element, {
            backgroundColor: '#ffffff',
            scale: 2,
            useCORS: true,
            logging: false,
        }).then(canvas => {
            // Restore visibility
            noExportElements.forEach(el => el.style.display = 'block');
            const link = document.createElement('a');
            let fileName = '';
            if (contractData?.['Booking Number']) {
                fileName = `Booking-${contractData['Booking Number']}.png`;
            } else if (lastDate) {
                fileName = `${formatDateToDMY(lastDate)}-records.png`;
            } else {
                const today = new Date();
                const todayStr = today.toISOString().slice(0, 10).split('-').reverse().join('-');
                fileName = `${todayStr}-records.png`;
            }
            link.download = fileName;
            link.href = canvas.toDataURL('image/png');
            link.click();
            toast.success('Image exported successfully!');

            const bookingId = contractData?.['Booking Number'] || 'Report';
            adminService.logUserAction('GENERATE_REPORT', 'Generated Mileage Report (Image)', currentUser, { bookingId: bookingId });
        });
    };

    const handleRefSubmit = async () => {
        // Invoice Generation Logic
        const pricePerKm = companySettings.price_per_km;
        if (missingDates) {
            number.setError('Enter the contract end date first: the allowed kilometres need the start and the end date.');
            return;
        }

        // Recalculate total used based on active mode
        let currentTotalUsed = 0;
        let activeLogs = [];

        if (contractHistory.length > 0) {
            contractHistory.forEach(c => {
                const log = vehicleLogs[c.id];
                if (log && log.out && log.in) {
                    const start = Number(log.out);
                    const end = Number(log.in);
                    if (!isNaN(start) && !isNaN(end) && end >= start) {
                        const diff = end - start;
                        currentTotalUsed += diff;
                        const plate = c['Plate No.'] || c['Plate Number'] || '';
                        const model = c['Model'] || c['Car Model'] || '';
                        activeLogs.push({
                            desc: `${model} (${plate})`,
                            out: start,
                            inVal: end,
                            diff: diff
                        });
                    }
                }
            });
        } else {
            // Legacy / Single Input Fallback
            currentTotalUsed = logs.reduce((acc, log) => acc + (log.inVal - log.out), 0);
            activeLogs = logs.map(l => ({ desc: 'Vehicle', out: l.out, inVal: l.inVal, diff: l.inVal - l.out }));
        }

        const exceededKm = Math.max(0, currentTotalUsed - allowedKm);
        // Net = exceeded km x the price per km; VAT 5 %; Total (the shared desk invoice - DUP-22)
        const amounts = deskInvoiceAmounts(exceededKm * pricePerKm);

        const customerName = contractData['Customer'] || 'Customer';
        const bookingId = contractData['Booking Number'] || '';
        const contractNo = (contractData['Contract No.'] || '').split('-')[0] || '';
        const model = contractData['Car Model'] || contractData['Model'] || '';
        const plateNo = contractData['Plate Number'] || contractData['Plate No.'] || '';
        const vehicle = model && plateNo ? `${model} - ${plateNo}` : (model || plateNo || 'Vehicle');
        const startDate = startShown;
        const endDate = endShown;

        // The number through the one invoice-number service (today: ALWFQ-<typed>, as before)
        let refNumber;
        try {
            [refNumber] = await number.claim(1, currentUser?.email || '');
        } catch {
            return; // the dialog shows why
        }

        const html = deskInvoiceHtml({
            title: 'Tax Invoice',
            date: new Date().toLocaleDateString('en-GB'),
            ref: refNumber,
            trn: companySettings.trn,
            subject: 'SUB: Micro Lease Cars',
            greeting: ['Dear Sir,', 'We thank you for your business renting the below vehicle;'],
            amountLabel: 'Exceed KM Amount',
            amounts,
            description: [
                escapeHtml(customerName),
                `Booking ID: ${escapeHtml(bookingId)}`,
                `R/A: ${escapeHtml(contractNo)}`,
                `Vehicle: ${escapeHtml(contractHistory.length > 1 ? 'Multiple Vehicles (See Below)' : vehicle)}`,
                `Start Date: ${escapeHtml(startDate)} - ${escapeHtml(endDate)}`,
                '',
                ...activeLogs.map((log) => `<strong>${log.desc !== 'Vehicle' ? `${escapeHtml(log.desc)}: ` : ''}</strong> Delivered: ${escapeHtml(log.out)} KM | Collected: ${escapeHtml(log.inVal)} KM`),
                '',
                `Total Used Kilometer = ${currentTotalUsed} KM`,
                `Total Allowed Kilometer = ${allowedKm} KM`,
                `Exceeded KM: ${exceededKm} KM x AED ${escapeHtml(pricePerKm)}`,
            ],
        });

        setShowRefModal(false);
        try {
            await saveDeskInvoicePdf(html, `Tax-Invoice-${bookingId || 'Unknown'}.pdf`);
        } catch (error) {
            logger.error('Failed to generate PDF:', error);
            toast.error('Failed to generate PDF.');
            return;
        }
        toast.success('Generated Invoice successfully!');

        adminService.logUserAction('GENERATE_REPORT', 'Generated Mileage Invoice (PDF)', currentUser, { bookingId: bookingId, amount: amounts.total, reference: refNumber });
    };

    const handleCalculateTotal = () => {
        // Multi-vehicle calculation logic
        const newLogs = [];
        let hasError = false;

        contractHistory.forEach(c => {
            const log = vehicleLogs[c.id];
            if (log && log.out && log.in) {
                const outNum = Number(log.out);
                const inNum = Number(log.in);

                if (isNaN(outNum) || isNaN(inNum) || outNum < 0 || inNum < 0) {
                    setInputError(`Invalid KM for ${c['Plate No.'] || c['Plate Number']}: Must be positive.`);
                    hasError = true;
                } else if (outNum > inNum) {
                    setInputError(`Invalid KM for ${c['Plate No.'] || c['Plate Number']}: OUT > IN.`);
                    hasError = true;
                } else {
                    newLogs.push({
                        date: c['Drop-off Date'] || c['Pick-up Date'] || 'N/A',
                        out: outNum,
                        inVal: inNum,
                        plate: c['Plate No.'] || c['Plate Number'] || c['Plate No'],
                        model: c['Model'] || c['Car Model'] || ''
                    });
                }
            }
        });

        if (hasError) return;

        if (newLogs.length === 0) {
            setInputError('Please enter KM for at least one vehicle.');
            return;
        }

        setLogs(newLogs);
        setInputError('');
    };

    const syncedMark = <Icon glyph={ICONS.cloud_sync} size="xs" tone="info" label="Synced from Archive" title="Synced from Archive" />;
    const eCheckLink = (num) => (
        <a
            href={`https://ejarprod.iyelo.com:6300/app/fleet-management/e-check/e-check-list/view/${num}`}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-purple-700 hover:underline"
        >
            Open E-Check <Icon glyph={ICONS.arrowRight} size="xs" />
        </a>
    );

    return (
        <div className="flex flex-col gap-4">
            <Card title="Mileage calculator" subtitle="Excess kilometres against 2,500 km per 30 days" padding="none">
                <div className="p-4 flex flex-col gap-6 bg-white" id="export-section">
                    {/* Booking Input */}
                    <div className="max-w-md no-export">
                        <RequirePermission
                            permission={PERMISSIONS.ops_mileage_booking_input}
                            fallback={(
                                <div>
                                    <div className="text-sm font-medium text-gray-700 mb-1">Booking or Contract Number</div>
                                    <p className="text-sm text-gray-500">Access Restricted</p>
                                </div>
                            )}
                        >
                            <Field label="Booking or Contract Number" error={error || undefined}>
                                <Input
                                    value={booking}
                                    onChange={(e) => setBooking(e.target.value)}
                                    placeholder="Enter Booking or Contract #"
                                    prefix={<Icon glyph={ICONS.search} size="sm" />}
                                />
                            </Field>
                        </RequirePermission>
                    </div>

                    {/* Contract Info */}
                    {contractData && (
                        <Card><div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-4 gap-x-8">
                            <div className="space-y-1">
                                <p className="text-xs font-medium text-gray-500">Contract details</p>
                                <p className="text-sm"><span className="text-gray-600">Contract #:</span> <span className="font-mono font-medium text-gray-900">{contractData['Contract No.'] || contractData['Contract No'] || 'N/A'}</span></p>
                                {contractData['Booking Number'] && (
                                    <p className="text-sm"><span className="text-gray-600">Booking ID:</span> <span className="text-gray-900">{contractData['Booking Number']}</span></p>
                                )}
                            </div>

                            <div className="space-y-1">
                                <p className="text-xs font-medium text-gray-500">Customer</p>
                                <p className="text-sm font-semibold text-gray-900">{contractData['Customer'] || 'Unknown Customer'}</p>
                                <p className="text-sm text-gray-600">
                                    Status: <StatusBadge status={contractSource ? contractSource.split(' ')[0] : ''} label={contractSource || 'N/A'} />
                                </p>
                            </div>

                            <div className="space-y-1">
                                <p className="text-xs font-medium text-gray-500">Timeline & vehicles</p>
                                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-700">
                                    <p><span className="text-gray-600">Start:</span> {startShown || 'N/A'}</p>
                                    <p><span className="text-gray-600">End:</span> {endShown || 'N/A'}</p>
                                </div>
                                <div className="max-w-[200px] no-export">
                                    <Field label="End date (for the allowed km)" error={missingDates ? 'Enter the end date' : undefined}>
                                        <Input size="sm" type="date" value={manualEndDate} onChange={(e) => setManualEndDate(e.target.value)} />
                                    </Field>
                                </div>
                                <p className="text-sm text-gray-700 leading-relaxed">
                                    <span className="text-gray-600">Cars ({contractHistory.length}):</span> {
                                        (() => {
                                            const plateGroups = {};
                                            contractHistory.forEach(c => {
                                                const p = c['Plate No.'] || c['Plate Number'] || c['Plate No'] || 'N/A';
                                                plateGroups[p] = (plateGroups[p] || 0) + 1;
                                            });
                                            return Object.entries(plateGroups).map(([plate, count], i) => (
                                                <span key={plate}>
                                                    {i > 0 && ', '}{plate} {count > 1 && <strong>({count})</strong>}
                                                </span>
                                            ));
                                        })()
                                    }
                                </p>
                            </div>
                        </div></Card>
                    )}

                    {/* Multi-Vehicle List */}
                    {contractHistory.length > 0 && (
                        <section className="max-w-3xl">
                            <h3 className="text-sm font-semibold text-gray-900 mb-3">Vehicle usage history</h3>
                            <div className="flex flex-col gap-3">
                                {contractHistory.map((car, _index) => {
                                    const plate = car['Plate No.'] || car['Plate Number'] || 'N/A';
                                    const model = car['Model'] || car['Car Model'] || 'N/A';
                                    const carLabel = `${model} - ${plate}`;
                                    const log = vehicleLogs[car.id] || { out: '', in: '' };

                                    return (
                                        <Card key={car.id}><div className="flex flex-col md:flex-row md:items-start gap-4">
                                            <div className="flex-1">
                                                <div className="font-medium text-gray-900">{carLabel}</div>
                                                <div className="text-xs text-gray-600">
                                                    Pick-up: <span className="font-medium">{showContractDate(car['Pick-up Date'])}</span>
                                                    {car['Drop-off Date'] ? (
                                                        <> | Drop-off: <span className="font-medium">{showContractDate(car['Drop-off Date'])}</span></>
                                                    ) : ' | (Active)'}
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-3 w-full md:w-80">
                                                <div>
                                                    <Field label="Start KM" labelAction={syncedFields[`${car.id}_out`] ? syncedMark : null}>
                                                        <Input
                                                            size="sm"
                                                            type="number"
                                                            value={log.out}
                                                            onChange={(e) => handleVehicleLogChange(car.id, 'out', e.target.value)}
                                                            placeholder="Start KM"
                                                            className="tabular-nums"
                                                        />
                                                    </Field>
                                                    {car['E-Check Number Out'] && eCheckLink(car['E-Check Number Out'])}
                                                </div>
                                                <div>
                                                    <Field label="End KM" labelAction={syncedFields[`${car.id}_in`] ? syncedMark : null}>
                                                        <Input
                                                            size="sm"
                                                            type="number"
                                                            value={log.in}
                                                            onChange={(e) => handleVehicleLogChange(car.id, 'in', e.target.value)}
                                                            placeholder="End KM"
                                                            className="tabular-nums"
                                                        />
                                                    </Field>
                                                    {car['E-Check Number In'] && eCheckLink(car['E-Check Number In'])}
                                                </div>
                                            </div>
                                        </div></Card>
                                    );
                                })}
                            </div>
                        </section>
                    )}

                    {/* Legacy Single Inputs (Only if no history - Fallback, though history should always exist if found) */}
                    {contractHistory.length === 0 && (
                        <div className="max-w-2xl">
                            <FormGrid cols={2}>
                                <Field label="Start KM (OUT)">
                                    <Input
                                        value={out}
                                        onChange={(e) => setOut(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && handleAddLog()}
                                        type="number"
                                        className="tabular-nums"
                                    />
                                </Field>
                                <Field label="End KM (IN)">
                                    <Input
                                        value={inVal}
                                        onChange={(e) => setInVal(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && handleAddLog()}
                                        type="number"
                                        ref={outInputRef}
                                        className="tabular-nums"
                                    />
                                </Field>
                            </FormGrid>
                        </div>
                    )}

                    {inputError && <p className="text-sm text-red-700" role="alert">{inputError}</p>}

                    <RequirePermission permission={PERMISSIONS.ops_mileage_add_trip} fallback={null}>
                        <div>
                            {contractHistory.length > 0 ? (
                                <Button variant="primary" onClick={handleCalculateTotal} leadingIcon={<Icon glyph={ICONS.calculator} size="sm" />}>
                                    Calculate Total Usage
                                </Button>
                            ) : (
                                <Button variant="primary" onClick={handleAddLog} leadingIcon={<Icon glyph={ICONS.plus} size="sm" />}>
                                    Add Trip
                                </Button>
                            )}
                        </div>
                    </RequirePermission>

                    {/* Logs Table */}
                    {logs.length > 0 && (
                        <TableContainer className="overflow-x-auto">
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableHeader className="w-10">#</TableHeader>
                                        <TableHeader>Vehicle Plate</TableHeader>
                                        <TableHeader>Type</TableHeader>
                                        <TableHeader>Out</TableHeader>
                                        <TableHeader>In</TableHeader>
                                        <TableHeader>Distance</TableHeader>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {logs.map((log, i) => (
                                        <TableRow key={i}>
                                            <TableCell><CellNumber value={i + 1} /></TableCell>
                                            <TableCell><CellPlate>{log.plate || 'N/A'}</CellPlate></TableCell>
                                            <TableCell><CellText>{log.model || 'N/A'}</CellText></TableCell>
                                            <TableCell><CellNumber value={log.out} /></TableCell>
                                            <TableCell><CellNumber value={log.inVal} /></TableCell>
                                            <TableCell><CellNumber value={log.inVal - log.out} /></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    {/* Results */}
                    {logs.length > 0 && (
                        <StatGrid min={180}>
                            <Stat label="Total used" value={`${totalUsedKm} km`} icon={ICONS.car} />
                            <Stat label="Allowed" value={missingDates ? '— (no end date)' : `${allowedKm} km`} icon={ICONS.check} />
                            <Stat label="Exceeded" value={missingDates ? '—' : `${exceeded} km`} icon={ICONS.alert} tone={exceeded > 0 ? 'danger' : undefined} />
                        </StatGrid>
                    )}

                    {/* Actions */}
                    {logs.length > 0 && (
                        <div className="ds-toolbar">
                            <RequirePermission permission={PERMISSIONS.ops_mileage_export_excel} fallback={null}>
                                <Button onClick={exportToExcel} leadingIcon={<Icon glyph={ICONS.spreadsheet} size="sm" />}>
                                    Export Excel
                                </Button>
                            </RequirePermission>

                            <RequirePermission permission={PERMISSIONS.ops_mileage_export_image} fallback={null}>
                                <Button onClick={exportAsImage} leadingIcon={<Icon glyph={ICONS.download} size="sm" />}>
                                    Export Image
                                </Button>
                            </RequirePermission>

                            <RequirePermission permission={PERMISSIONS.ops_mileage_generate_invoice} fallback={null}>
                                <Button onClick={() => setShowRefModal(true)} leadingIcon={<Icon glyph={ICONS.invoices} size="sm" />}>
                                    Generate VAT Invoice
                                </Button>
                            </RequirePermission>
                        </div>
                    )}
                </div>
            </Card>

            {/* Invoice Modal */}
            <Dialog
                open={showRefModal}
                onClose={() => setShowRefModal(false)}
                title="Confirm invoice"
                size="sm"
                closeOnOverlay={false}
                footer={(
                    <>
                        <Button onClick={() => setShowRefModal(false)}>Cancel</Button>
                        <Button variant="primary" onClick={handleRefSubmit}>Generate</Button>
                    </>
                )}
            >
                <div className="flex flex-col gap-4">
                    {missingDates ? (
                        <p className="text-sm text-red-700" role="alert">Enter the contract end date first: the allowed kilometres need the start and the end date.</p>
                    ) : (
                        // the amount the invoice will carry: exceeded km x the price per km, + VAT 5% (was km x 1.05)
                        <p className="text-sm text-gray-700">Total Amount: <span className="font-semibold text-gray-900 tabular-nums">AED {deskInvoiceAmounts(exceeded * companySettings.price_per_km).total.toFixed(2)}</span> (Inc. VAT) · {exceeded} km x AED {companySettings.price_per_km}</p>
                    )}
                    <InvoiceNumberField state={number} label="Reference Number" placeholder="e.g. 1234" prefix="" optional />
                </div>
            </Dialog>
        </div>
    );
};

export default MileageCalculator;
